import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarCheck, CheckCircle2, ChevronDown, Loader2, MessageCircleQuestion, PhoneCall, RefreshCw, Sparkles, Store, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const BENEFITS = [
  { icon: Store, t: "Understands your business", d: "Services, prices, hours and policies." },
  { icon: MessageCircleQuestion, t: "Handles customer enquiries", d: "Answers supported questions using your business information." },
  { icon: CalendarCheck, t: "Helps manage bookings", d: "Confirms only eligible bookings and safely captures other requests." },
];
const STEPS = ["Connecting to your business", "Discovering services and prices", "Checking staff and opening hours", "Validating booking rules", "Preparing Shally"];
const STATUS = {
  ready: { icon: CheckCircle2, label: "Ready", tone: "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100" },
  limited: { icon: AlertTriangle, label: "Ready with limitations", tone: "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100" },
  action_needed: { icon: XCircle, label: "Action needed", tone: "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100" },
};
const CLASSES = [
  ["confirms", "Confirms bookings", "Booked on the call when a scheduled team member is free."],
  ["requests", "Accepts requests", "Saved for your team to confirm."],
  ["special", "Requires special handling", "Saved for professional review, e.g. surgery."],
  ["needs_info", "Needs information", "Missing or conflicting price, duration or name."],
  ["answers", "Answers enquiries", "Shally can explain these with their verified price."],
];

function Hero({ onStart, busy, connected }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 dark:border-slate-800 dark:bg-slate-900" data-testid="shally-hero">
      <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Sparkles className="h-5 w-5" aria-hidden /></div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-white" data-testid="shally-hero-title">Meet Shally, Your AI Receptionist</h1>
      <p className="mt-2 max-w-2xl text-base text-slate-600 dark:text-slate-300">Let Shally learn your business automatically. Pawkt will use your existing services, prices, opening hours and business rules to prepare your AI receptionist.</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {BENEFITS.map(({ icon: I, t, d }) => (
          <div key={t} className="rounded-xl border border-slate-200 p-4 dark:border-slate-800" data-testid="shally-benefit">
            <I className="h-5 w-5 text-primary" aria-hidden /><p className="mt-2 font-semibold text-slate-900 dark:text-white">{t}</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{d}</p>
          </div>))}
      </div>
      <Button size="lg" className="mt-6 h-12 w-full rounded-xl text-base sm:w-auto" onClick={onStart} disabled={busy} data-testid="shally-setup-btn">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : connected ? <RefreshCw className="mr-2 h-4 w-4" /> : <Sparkles className="mr-2 h-4 w-4" />}
        {busy ? "Setting up Shally…" : connected ? "Refresh Shally Setup" : "Connect & Set Up Shally"}
      </Button>
    </section>);
}

function Progress({ steps, busy }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!steps) { setShown(0); return; }
    const t = setInterval(() => setShown((n) => (n >= steps.length ? n : n + 1)), 220);
    return () => { clearInterval(t); };
  }, [steps]);
  if (!busy && !steps) return null;
  return (
    <ol className="space-y-2 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900" aria-live="polite" data-testid="shally-progress">
      {STEPS.map((label, i) => {
        const s = steps && i < shown ? steps[i] : null;
        return (
          <li key={label} className="flex items-start gap-3 text-sm" data-testid={`shally-step-${i}`}>
            {s ? (s.ok ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-label="done" /> : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-label="needs attention" />)
              : <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-slate-400" aria-label="in progress" />}
            <span><span className="font-medium text-slate-900 dark:text-white">{label}</span>
              <span className="block text-slate-500 dark:text-slate-400">{s ? s.detail : "Working…"}</span></span>
          </li>);
      })}
    </ol>);
}

function Details({ r }) {
  return (
    <details className="group mt-4 rounded-xl border border-current/15 bg-white/60 p-3 dark:bg-slate-900/40" data-testid="shally-details">
      <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">View details<ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" /></summary>
      <ul className="mt-3 space-y-3">
        {CLASSES.map(([k, t, d]) => r.classes[k].count > 0 && (
          <li key={k} className="text-sm text-slate-700 dark:text-slate-300" data-testid={`shally-class-${k}`}>
            <p className="font-semibold text-slate-900 dark:text-white">{t} · {r.classes[k].count}</p><p className="text-xs text-slate-500">{d}</p>
            <p className="mt-1 break-words text-xs">{r.classes[k].services.slice(0, 12).join(" · ")}{r.classes[k].count > 12 ? ` · +${r.classes[k].count - 12} more` : ""}</p>
          </li>))}
        {r.tips.map((t) => <li key={t} className="text-xs text-slate-600 dark:text-slate-400">• {t}</li>)}
      </ul>
    </details>);
}

function Result({ r }) {
  const S = STATUS[r.status], I = S.icon;
  return (
    <section className={`rounded-2xl border p-5 sm:p-6 ${S.tone}`} data-testid="shally-result" data-status={r.status}>
      <div className="flex items-start gap-3"><I className="h-7 w-7 shrink-0" aria-hidden />
        <div className="min-w-0"><p className="text-lg font-bold" data-testid="shally-result-label">{S.label}</p><p className="text-sm" data-testid="shally-result-headline">{r.headline}</p></div></div>
      {r.action && <Button asChild variant="outline" className="mt-4 h-11 rounded-xl bg-white text-slate-900 dark:bg-slate-900 dark:text-white"><Link to={r.action.href} data-testid="shally-result-action">{r.action.label}</Link></Button>}
      <Details r={r} />
    </section>);
}

function Activation({ a }) {
  if (!a) return null;
  const go = () => document.getElementById("shally-advanced")?.setAttribute("open", "") || document.getElementById("rcp-activate-step")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900" data-testid="shally-activation">
      <div className="flex items-start gap-3"><PhoneCall className="h-6 w-6 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1"><p className="font-semibold text-slate-900 dark:text-white">{a.enabled ? "Shally is answering calls on this number" : "Activate Shally on this number"}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">{a.enabled ? "You can turn her off any time in Advanced settings." : a.can_activate ? "Everything is in place. Activation is a separate step and needs your confirmation." : "Setup never switches on a phone number. Before activation:"}</p>
          {!a.enabled && !a.can_activate && <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-300" data-testid="shally-activation-missing">{a.missing.map((m) => <li key={m}>• {m}</li>)}</ul>}
        </div></div>
      {!a.enabled && <Button variant={a.can_activate ? "default" : "outline"} className="mt-4 h-11 w-full rounded-xl sm:w-auto" onClick={go} data-testid="shally-activate-open">{a.can_activate ? "Review and activate" : "Open activation settings"}</Button>}
    </section>);
}

export function ShallyOneClick({ bid }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false), [err, setErr] = useState(""), [run, setRun] = useState(null);
  const { data } = useQuery({ queryKey: ["shally-auto", bid], enabled: !!bid, retry: 1, queryFn: async () => (await api.get(`/receptionist/setup/${bid}/auto-setup`)).data });
  useEffect(() => { setRun(null); setErr(""); }, [bid]);
  const start = async () => {
    if (busy) return;
    setBusy(true); setErr(""); setRun(null);
    try { const r = (await api.post(`/receptionist/setup/${bid}/auto-setup`)).data; setRun(r); qc.setQueryData(["shally-auto", bid], r); qc.invalidateQueries({ queryKey: ["rcp-setup"] }); qc.invalidateQueries({ queryKey: ["shally-onb", bid] }); }
    catch (e) { setErr(e?.response?.data?.detail || "We couldn't finish setting up Shally. Nothing was changed. Please try again."); }
    finally { setBusy(false); }
  };
  const r = run || (data?.connected ? data : null);
  return (
    <div className="space-y-4" data-testid="shally-one-click">
      <Hero onStart={start} busy={busy} connected={!!data?.connected} />
      <Progress steps={run?.steps || null} busy={busy} />
      {err && <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100" data-testid="shally-error">
        <XCircle className="h-5 w-5" aria-hidden /><span className="flex-1">{err}</span><Button size="sm" variant="outline" onClick={start} data-testid="shally-retry">Try again</Button></div>}
      {r && !busy && <Result r={r} />}
      {r && !busy && <Activation a={r.activation} />}
    </div>);
}
